import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { getAssigneeListDetails, getUsersByIds } from '../../../../repository';
import { buildExpressQuery } from '../../../../repository/queries/builders';
import getUserGroupsByIds from '../../../../repository/queries/getUserGroupsByIds';
import useExpress from '../../../../repository/react-query/useExpress';
import { PAY_FILTER } from './constants';
import { toSentenceCase, toTitleCase } from '../../../../../utils';

const getPaymentLineItemConfigs = (startDate, endDate, rateId, paymentType) => {
    const params = {};
    params.filter = {
        __t: { $ne: 'Loan' },
    };

    // Apply date filtering if startDate and endDate are provided
    if (startDate && endDate) {
        const start = moment(startDate).startOf('day').toISOString();
        const end = moment(endDate).endOf('day').toISOString();
        params.filter = {
            start_date: { $lte: end },
            $or: [{ end_date: { $gte: start } }, { end_date: { $eq: null } }],
            ...params.filter,
        };
    }

    // Apply rate filtering if rateId is provided
    if (rateId) {
        params.filter.payment_rate = rateId;
    }

    if (paymentType) {
        params.filter.payment_type = paymentType;
    }

    const endpoint = '/payment-line-item-config';
    const request = useExpress({ endpoint, params });

    /**
     * This gets the payer and payee information based on Users, Assignees and User groups.
     * This is needed because the payer_filter.ids references to both UserModel and AssigneeModel therefore using promise.all to individually fetch instead of mongoose populate as the result is getting overridden.
     */
    return async function () {
        const paymentLineItemConfigs = await request();
        const { userIds, userGroupIds, assigneeIds } = paymentLineItemConfigs.reduce(
            (acc, config) => {
                if (config?.payee_filter?.role === 'assignee') {
                    (config.payee_filter?.ids ?? []).forEach((id) => acc.assigneeIds.add(id));
                } else {
                    (config?.payee_filter?.ids ?? []).forEach((id) => acc.userIds.add(id));
                    (config?.payee_filter?.user_group ?? []).forEach((id) => acc.userGroupIds.add(id));
                }
                (config.payer_filter?.ids ?? []).forEach((id) => acc.userIds.add(id));
                (config.payer_filter?.user_group ?? []).forEach((id) => acc.userGroupIds.add(id));
                return acc;
            },
            { userIds: new Set(), userGroupIds: new Set(), assigneeIds: new Set() }
        );

        // May need to update all of these to body-query versions if id list becomes too large
        const [users, { assignees }, userGroups] = await Promise.all([getUsersByIds([...userIds], ['name'], buildExpressQuery), getAssigneeListDetails({ ids: [...assigneeIds], fields: ['assignee_name'] }), getUserGroupsByIds({ ids: [...userGroupIds] })]);

        const payerAndPayees = [...(users ?? []), ...(assignees ?? []), ...(userGroups ?? [])];
        const payerPayeesMap = new Map(payerAndPayees.map((entity) => [entity._id.toString(), entity]));

        const res = paymentLineItemConfigs.map(({ payee_filter, payer_filter, ...rest }) => {
            const payers = new Map();
            const payees = new Map();

            const addValueToSet = (value, map, filterType) => {
                const existingSet = map.get(filterType) || new Set();
                if (Array.isArray(value)) {
                    value.forEach((item) => existingSet.add(item));
                } else {
                    existingSet.add(value);
                }
                map.set(filterType, existingSet);
            };

            const addEntityNameToSet = (id, map, filterType = PAY_FILTER.USER_IDS) => {
                const entity = payerPayeesMap.get(id.toString());
                if (entity) {
                    const name = entity.name?.trim() ?? entity.assignee_name?.trim();
                    if (name) {
                        addValueToSet(name, map, filterType);
                    }
                }
            };

            const addNames = (filter, map) => {
                if (filter.ids) {
                    filter.ids.forEach((id) => addEntityNameToSet(id, map, PAY_FILTER.USER_IDS));
                }

                if (filter.user_group) {
                    filter.user_group.forEach((id) => addEntityNameToSet(id, map, PAY_FILTER.USER_GROUP));
                }

                Object.values(PAY_FILTER)
                    .filter((filterType) => filterType !== PAY_FILTER.USER_IDS && filterType !== PAY_FILTER.USER_GROUP && filter[filterType]) // Skip USER_IDS and USER_GROUP as they're handled separately
                    .forEach((filterType) => addValueToSet(filter[filterType], map, filterType));

                if (map.size === 0 && filter.role) {
                    addValueToSet(`${filter.role?.toUpperCase()}S`, map, 'All');
                }
            };

            addNames(payee_filter ?? [], payees);
            addNames(payer_filter ?? [], payers);

            const formatNames = (map, separator) => {
                const parts = [];
                // eslint-disable-next-line no-restricted-syntax
                for (const [key, value] of map) {
                    if (value.size > 0) {
                        const formattedKey = key === PAY_FILTER.USER_IDS ? '' : `${toTitleCase(toSentenceCase(key))}: `;
                        const formattedValues = toTitleCase(toSentenceCase([...value].join(', ')));
                        parts.push(`${formattedKey}${formattedValues}`);
                    }
                }

                return parts.join(separator);
            };

            return {
                ...rest,
                payee_filter,
                payer_filter,
                payerName: formatNames(payers, '\n\n'),
                payeeName: formatNames(payees, '\n\n'),
                payerNameCsv: formatNames(payers, '; '),
                payeeNameCsv: formatNames(payees, '; '),
            };
        });
        return res;
    };
};

export const usePaymentLineItemConfigs = ({ startDate, endDate, rateId, paymentType, enabled = true } = {}) => {
    const queryFn = getPaymentLineItemConfigs(startDate, endDate, rateId, paymentType);
    const queryKey = ['payment-line-item-configs', startDate, endDate, rateId, paymentType];
    return useQuery({ queryKey, queryFn, enabled });
};

export const getPaymentLineItemConfig = (configId) => {
    const endpoint = `/payment-line-item-config/${configId}`;
    return useExpress({ endpoint });
};

export const usePaymentLineItemConfig = ({ configId, enabled = true } = {}) => {
    const queryFn = getPaymentLineItemConfig(configId);
    const queryKey = ['payment-line-item-config', configId];
    return useQuery({ queryKey, queryFn, enabled, cacheTime: 0 });
};

export const getPaymentLineItemRate = (rateId) => {
    const endpoint = '/payment-line-item-rate';
    const params = {
        where: {
            id: rateId,
        },
    };
    return useExpress({ endpoint, params });
};

export const usePaymentLineItemRate = ({ rateId, enabled = true } = {}) => {
    const queryFn = getPaymentLineItemRate(rateId);
    const queryKey = ['payment-line-item-rate', rateId];
    return useQuery({ queryKey, queryFn, enabled, cacheTime: 0 });
};
